import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State
} from '@stencil/core';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import { CheckboxDisplay } from './jui-checkbox-group.model';
import {
  renderFormError,
  renderTooltipOnLeft,
  renderTooltipOnBottom
} from '../../../shared/form-controls/jui-form-control.utils';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';

@Component({
  tag: 'jui-checkbox-group',
  styleUrl: 'jui-checkbox-group.scss',
  shadow: true
})
export class JuiCheckboxGroup implements ComponentInterface {
  /**
   * Label to display for the checkbox group input.
   */
  @Prop() label = '';

  /**
   * The form name for the checkbox group control.
   */
  @Prop() name = '';

  /**
   * Display can be stacked (default) or inline.
   */
  @Prop() display: CheckboxDisplay = CheckboxDisplay.stacked;

  /**
   * Value to hold the current value for the checkbox group.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true }) value = '';

  /**
   * Marking the checkbox group as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Marking the checkbox group as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * Set the size of the checkbox-group. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * The errors for the input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Internal prop
   */
  @Prop() storybook = false;

  @State()
  private errorMessages = null;

  @State()
  private touched = false;

  @State()
  private currentSelectedValues = [];

  @State()
  private currentSelectedValuesString = null;

  @State()
  private checkboxes: HTMLJuiCheckboxElement[] = [];

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  @Element() el: HTMLElement;

  @Event() public juiChange: EventEmitter<number | string>;

  @Listen('juiCheckboxChecked')
  juiCheckedListener(evt: CustomEvent): void {
    this.updateSelectedValues(evt);
    this.value = this.currentSelectedValuesString;
    this.juiChange.emit(this.value);
  }

  componentWillLoad(): void {
    this.setSelectedValues();

    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  componentDidLoad(): void {
    this.initCheckboxItems();
  }

  componentWillUpdate(): void {
    this.renderError();
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  private updateSelectedValues(event: CustomEvent): void {
    // Derive from the value prop to stay in sync with programmatic changes
    const currentValues = this.value ? this.value.split(',') : [];

    if (event.detail.checked) {
      if (!currentValues.includes(event.detail.value)) {
        currentValues.push(event.detail.value);
      }
    } else {
      const foundIdx = currentValues.indexOf(event.detail.value);
      if (foundIdx !== -1) {
        currentValues.splice(foundIdx, 1);
      }
    }

    this.currentSelectedValues = currentValues;
    this.currentSelectedValuesString = currentValues.join() || null;
  }

  private setSelectedValues(): void {
    if (this.value) {
      this.checkboxes = this.getCheckboxes();
      const checkedItems = this.value.split(',');

      if (checkedItems.length) {
        this.checkboxes.forEach((x: HTMLJuiCheckboxElement): void => {
          if (checkedItems.includes(x.value)) {
            x.setAttribute('checked', 'true');
            this.currentSelectedValues.push(x.value);
          }
        });
      }
    }
  }

  private getCheckboxes(): HTMLJuiCheckboxElement[] {
    return Array.from(this.el.children) as HTMLJuiCheckboxElement[];
  }

  private initCheckboxItems(): void {
    this.checkboxes.forEach((checkbox: HTMLJuiCheckboxElement): void => {
      if (this.disable) {
        // if group is disabled the disable all children
        checkbox['disable'] = true;
      }
      // update checkbox sizes from the group
      checkbox.size = this.size;

      checkbox.single = false;
    });
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;

    const helpText = this.help ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div class="jui-control">
          <fieldset
            role="group"
            class={{
              'jui-control__fieldset': true,
              [`jui-control__fieldset--${this.display}`]: true,
              [`jui-control__fieldset--${this.size}`]: true,
              'jui-control__fieldset--invalid': !!this.errors && this.touched
            }}
          >
            <legend
              {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
              class={{
                'jui-control__legend': true,
                'jui-control__legend--has-value': !!this.value,
                'jui-control__legend--disabled': !!this.disable,
                'jui-control__legend--has-tooltip': !!this.tooltip
              }}
            >
              <div
                class={{
                  'jui-control__legend-text': true
                }}
              >
                {this.required ? `${this.label} *` : this.label}
              </div>
              {tooltip}
            </legend>
            {helpText}
            <slot></slot>
          </fieldset>
          {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el, 'jui-control__fieldset');
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el, 'jui-control__fieldset');
    }
  }

  private renderError(): void {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
